import React, { useState } from "react";
import { Alert, AlertTitle, AlertDescription } from "ui/components/alert";
import { Button } from "ui/components/button";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "ui/components/collapsible";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "ui/components/card";
import { AlertTriangle } from "lucide-react";

// Creating a functional error boundary requires a wrapper since
// error boundaries must be class components in React
const ErrorBoundary = ({ children, fallback }) => {
  // This component will re-render children on error
  const [errorBoundaryKey, setErrorBoundaryKey] = useState(0);

  // This is the actual error boundary implementation
  return (
    <ClassErrorBoundary
      key={errorBoundaryKey}
      fallback={fallback}
      resetKey={() => setErrorBoundaryKey((prev) => prev + 1)}
    >
      {children}
    </ClassErrorBoundary>
  );
};

// We still need a class component for the actual error boundary functionality
class ClassErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Either use the provided fallback or our default UI
      return (
        this.props.fallback || (
          <ErrorFallbackComponent
            error={this.state.error}
            resetErrorBoundary={this.props.resetKey}
          />
        )
      );
    }

    return this.props.children;
  }
}

// Separate the fallback UI into its own functional component using shadcn components
const ErrorFallbackComponent = ({ error, resetErrorBoundary }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center  p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-red-600" />
            <CardTitle className="text-red-600">Something went wrong</CardTitle>
          </div>
          <CardDescription className="text-gray-600">
            We're sorry for the inconvenience.
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-6">
          <Alert variant="destructive" className="mb-6">
            <AlertTitle>An error occurred</AlertTitle>
            <AlertDescription className="text-sm">
              The application encountered an unexpected error.
            </AlertDescription>
          </Alert>

          {error && (
            <Collapsible
              open={isOpen}
              onOpenChange={setIsOpen}
              className="mt-4 w-full rounded border border-gray-200"
            >
              <CollapsibleTrigger asChild>
                <Button
                  variant="outline"
                  className="flex w-full justify-between"
                >
                  <span>Error Details</span>
                  <span>{isOpen ? "▲" : "▼"}</span>
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="overflow-auto bg-gray-50 p-4">
                  <pre className="text-xs text-gray-800 whitespace-pre-wrap">
                    {error.toString()}
                  </pre>
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </CardContent>

        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={() => window.location.reload()}>
            Reload Page
          </Button>
          <Button variant="default" onClick={() => resetErrorBoundary()}>
            Try Again
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export { ErrorBoundary };
